import React, { useEffect, useState } from 'react';
import { Spin, Radio } from '@douyinfe/semi-ui';
import { API } from '../helpers';

const Stat = ({ label, value, suffix }) => (
  <div style={{
    background: '#fff',
    padding: 16,
    borderRadius: 10,
    border: '1px solid #e5e6eb',
    minWidth: 0,
  }}>
    <div style={{ fontSize: 12, color: '#86909c' }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4, color: '#1f2329' }}>
      {(value ?? 0).toLocaleString()}
      {suffix && <span style={{ fontSize: 13, color: '#86909c', marginLeft: 4 }}>{suffix}</span>}
    </div>
  </div>
);

// 中文星期几
function weekdayLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const names = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
  return names[d.getDay()];
}

// 7 天趋势柱状图(支持切换 calls / quota / success_rate)
const TrendBars = ({ byDay, metric }) => {
  const [hover, setHover] = useState(null);
  const raw = Array.isArray(byDay) ? byDay : [];
  // 补齐最近 7 天:缺数据的日期占位(值=0),保持柱图始终 7 根,布局稳定
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const list = [];
  const map = new Map(raw.map((d) => [d.day, d]));
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today.getTime() - i * 86400000);
    const key = d.toISOString().slice(0, 10);
    const found = map.get(key);
    list.push(found || { day: key, calls: 0, quota: 0, success_calls: 0, success_rate: null });
  }
  const values = list.map((d) => {
    if (metric === 'quota') return Number(d.quota || 0);
    if (metric === 'success_rate') return Number(d.success_rate || 0);
    return Number(d.calls || 0);
  });
  const max = Math.max(1, ...values);
  const colorMap = {
    calls:        { bar: '#22c55e', soft: '#dcfce7' },
    quota:        { bar: '#3b82f6', soft: '#dbeafe' },
    success_rate: { bar: '#10b981', soft: '#d1fae5' },
  };
  const color = colorMap[metric] || colorMap.calls;
  const TODAY = new Date();
  const todayKey = TODAY.toISOString().slice(0, 10);
  const fmtVal = (v) => metric === 'success_rate' ? v.toFixed(1) + '%' : v.toLocaleString();
  const unit = metric === 'quota' ? 'quota' : metric === 'success_rate' ? '%' : '次';
  // 柱体宽度:7 根均分,按容器宽度自适应
  const barMaxWidth = 56;
  const barMinWidth = 16;
  const barWidth = Math.max(barMinWidth, Math.min(barMaxWidth, 70)); // 70% 子列宽

  return (
    <div style={{ position: 'relative' }}>
      {/* 单位标签:居中显示在柱图上方 */}
      <div style={{ textAlign: 'center', fontSize: 11, color: '#86909c', marginBottom: 6 }}>
        单位:{unit} · 7 天滚动窗口 · 当日柱体加粗 · 悬停查看明细 · 无数据日灰色占位
      </div>
      {list.every((d) => (metric === 'quota' ? !d.quota : metric === 'success_rate' ? d.success_rate == null : !d.calls)) ? (
        <div style={{ color: '#c9cdd4', textAlign: 'center', padding: 30 }}>暂无数据</div>
      ) : (
        // 统一坐标系:
        //   绘图区(plot area): 高度 120px,柱体在这范围内
        //   X 轴文字区(x-axis labels): 高度 ~36px,值/日期/周几 三行
        //   Y 轴容器高度 = 120 + 36 = 156(覆盖绘图区+标签)
        //   "0" 标签 top=120(对齐绘图区底边 + 标签顶部间距 4px)
        <div style={{ display: 'flex', padding: '4px 0 0' }}>
          {/* Y 轴刻度 */}
          <div style={{ position: 'relative', width: 52, flexShrink: 0, height: 156 }}>
            {[max, Math.round(max * 0.5), 0].map((v, i) => (
              <div key={i} style={{
                position: 'absolute', right: 6, fontSize: 10, color: '#a3a8b3',
                lineHeight: '12px',
                // 三刻度数字精确落在绘图区(120px)三个位置:
                //   max → 文字底部对齐 0px(顶端) → top:0 + translateY(0),数字向下展开到 12px
                //   max/2 → 文字中心对齐 60px → top:60 + translateY(-50%)
                //   0 → 文字顶部对齐 120px(底端) → top:120 + translateY(-100%),数字底部在 120
                top: i === 0 ? 0 : i === 1 ? 60 : 120,
                transform: i === 0 ? 'translateY(0)' : i === 1 ? 'translateY(-50%)' : 'translateY(-100%)',
              }}>{fmtVal(v)}</div>
            ))}
          </div>

          {/* 右侧整列:绘图区 + X 轴标签 */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* 绘图区 120px 高 */}
            <div style={{ position: 'relative', height: 120, borderLeft: '1px solid #eef0f3', borderBottom: '1px solid #eef0f3' }}>
              {/* 网格线 */}
              {[0, 60, 120].map((y) => (
                <div key={y} style={{
                  position: 'absolute', left: 0, right: 0, top: (120 - y) + 'px',
                  borderTop: y === 0 ? 'none' : '1px dashed #f0f1f3',
                  pointerEvents: 'none',
                }} />
              ))}

              <div style={{ display: 'flex', alignItems: 'flex-end', height: 120, gap: 0, padding: '0 12px' }}>
                {list.map((d, i) => {
                  const v = values[i];
                  const h = Math.max(0, (v / max) * 120);
                  const isToday = d.day === todayKey;
                  const isHover = hover === i;
                  const hasData = (metric === 'quota' ? d.quota > 0 : metric === 'success_rate' ? d.success_rate != null : d.calls > 0);
                  return (
                    <div key={d.day}
                      onMouseEnter={() => setHover(i)}
                      onMouseLeave={() => setHover(null)}
                      style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', position: 'relative', cursor: hasData ? 'pointer' : 'default' }}
                    >
                      {isHover && hasData && (
                        <div style={{
                          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                          background: 'rgba(31,35,41,0.92)', color: '#fff', padding: '6px 10px', borderRadius: 6,
                          fontSize: 12, whiteSpace: 'nowrap', zIndex: 10, pointerEvents: 'none', marginBottom: 4,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        }}>
                          <div style={{ fontWeight: 600 }}>{d.day} {weekdayLabel(d.day)}</div>
                          <div>调用: {d.calls || 0} 次</div>
                          <div>quota: {d.quota || 0}</div>
                          <div>成功率: {d.success_rate == null ? '—' : d.success_rate + '%'}</div>
                        </div>
                      )}
                      <div style={{
                        width: barWidth + '%', maxWidth: barMaxWidth, minWidth: barMinWidth,
                        height: h + 'px',
                        background: !hasData ? '#eef0f3' : (isHover ? color.bar : (isToday ? color.bar : color.bar + 'cc')),
                        borderRadius: '4px 4px 0 0',
                        transition: 'all 0.15s',
                        opacity: hover != null && !isHover ? 0.5 : 1,
                      }} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* X 轴文字区(独立 flex,与绘图区等宽均分 7 列) */}
            <div style={{ display: 'flex', padding: '4px 12px 0' }}>
              {list.map((d, i) => {
                const v = values[i];
                const isToday = d.day === todayKey;
                const hasData = (metric === 'quota' ? d.quota > 0 : metric === 'success_rate' ? d.success_rate != null : d.calls > 0);
                return (
                  <div key={d.day}
                    style={{ flex: 1, minWidth: 0, textAlign: 'center' }}
                  >
                    <div style={{ fontSize: 11, color: isToday ? '#1d7dfa' : (hasData ? '#52525b' : '#c9cdd4'), fontWeight: isToday ? 600 : 400 }}>
                      {hasData ? fmtVal(v) : '—'}
                    </div>
                    <div style={{ fontSize: 10, color: isToday ? '#1d7dfa' : '#86909c', marginTop: 2 }}>
                      {d.day.slice(5)}
                    </div>
                    <div style={{ fontSize: 9, color: '#c9cdd4', marginTop: 2 }}>{weekdayLabel(d.day)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminStats = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gwOnline, setGwOnline] = useState(null);
  const [trendMetric, setTrendMetric] = useState('calls');

  useEffect(() => {
    API.get('/api/admin/stats/overview')
      .then((r) => setData(r.data.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const poll = () => {
      fetch('/api/status').then((r) => r.json()).then((j) => {
        setGwOnline(j?.data?.online ?? null);
      }).catch(() => setGwOnline(false));
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => clearInterval(id);
  }, []);

  if (loading) return <Spin spinning />;
  if (!data) return <div>暂无数据</div>;

  return (
    <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 16px' }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>网关总览</h2>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
                        color: gwOnline === null ? '#86909c' : (gwOnline ? '#00b42a' : '#f56565') }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%',
                          background: gwOnline === null ? '#c9cdd4' : (gwOnline ? '#22c55e' : '#f56565') }} />
          {gwOnline === null ? '检测中' : (gwOnline ? '网关在线' : '网关离线')}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <Stat label="令牌总金额" value={`¥${Number(data.total.tokens_value ?? 0).toFixed(2)}`} />
        <Stat label="已启用套餐" value={data.total.plans_enabled} />
        <Stat label="待售令牌"   value={data.total.tokens_unsold} />
        <Stat label="24h 活跃 token" value={data.total.tokens_active_24h} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <Stat label="24h 调用"        value={data.last_24h?.calls} />
        <Stat label="24h 成功率"      value={data.last_24h?.success_rate ?? '—'} suffix="%" />
        <Stat label="24h 消耗 quota"  value={data.last_24h?.quota} />
        <Stat label="7d 调用"         value={data.last_7d?.calls} />
      </div>

      <div style={{ background: '#fff', borderRadius: 10, padding: 16, border: '1px solid #e5e6eb', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>过去 7 天调用趋势</h3>
          <Radio.Group
            type='button'
            size='small'
            value={trendMetric}
            onChange={(e) => setTrendMetric(e.target.value)}
            options={[
              { label: '调用次数', value: 'calls' },
              { label: '消耗 quota', value: 'quota' },
              { label: '成功率', value: 'success_rate' },
            ]}
          />
        </div>
        <TrendBars byDay={data.by_day} metric={trendMetric} />
      </div>

      <div style={{ background: '#fff', borderRadius: 10, padding: 16, border: '1px solid #e5e6eb' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>按模型分布(7d)</h3>
        {data.by_model.length === 0 ? (
          <div style={{ color: '#c9cdd4', textAlign: 'center', padding: 30 }}>暂无数据</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: '#86909c', fontSize: 12, textAlign: 'left' }}>
                <th style={{ padding: 8 }}>模型</th>
                <th style={{ padding: 8 }}>调用</th>
                <th style={{ padding: 8 }}>quota 消耗</th>
              </tr>
            </thead>
            <tbody>
              {data.by_model.map((m) => (
                <tr key={m.model} style={{ borderTop: '1px solid #f0f1f3' }}>
                  <td style={{ padding: 8, fontFamily: 'monospace' }}>{m.model}</td>
                  <td style={{ padding: 8 }}>{m.calls}</td>
                  <td style={{ padding: 8 }}>{m.quota}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminStats;
